// Loads the HERE Maps API for JavaScript (v3.1) on demand and hands back a shared
// H.service.Platform. Replaces the Google Maps JS loader — HERE is EU-hosted, so no
// end-user IP/fingerprint is sent to a US map host on page load.
//
// The API key comes from <meta name="here-api-key"> (see the app layouts).

const HERE_VERSION = "3.1"
const BASE = `https://js.api.here.com/v3/${HERE_VERSION}`
// core must load before the modules that extend H (service/mapevents/ui).
const CORE = `${BASE}/mapsjs-core.js`
const MODULES = [`${BASE}/mapsjs-service.js`, `${BASE}/mapsjs-mapevents.js`, `${BASE}/mapsjs-ui.js`]
const UI_CSS = `${BASE}/mapsjs-ui.css`

let herePromise = null
let platform = null

export function hereApiKey() {
  return document.querySelector('meta[name="here-api-key"]')?.content
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const script = document.createElement("script")
    script.src = src
    script.async = true
    script.onload = resolve
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })
}

function loadStylesheet(href) {
  if (document.querySelector(`link[href="${href}"]`)) return
  const link = document.createElement("link")
  link.rel = "stylesheet"
  link.type = "text/css"
  link.href = href
  document.head.appendChild(link)
}

// Resolves once window.H and all HERE modules are ready. Idempotent.
export function loadHereMaps() {
  if (window.H?.service && window.H?.mapevents && window.H?.ui) return Promise.resolve()

  if (!herePromise) {
    herePromise = loadScript(CORE)
      .then(() => Promise.all(MODULES.map(loadScript)))
      .then(() => loadStylesheet(UI_CSS))
      .catch((error) => {
        herePromise = null
        throw error
      })
  }
  return herePromise
}

// Shared platform instance (one per page). Call only after loadHereMaps() resolves.
export function herePlatform() {
  if (!platform) {
    const apikey = hereApiKey()
    if (!apikey) throw new Error("HERE API key not found")
    platform = new window.H.service.Platform({ apikey })
  }
  return platform
}
